import { defineProperty as _defineProperty, objectWithoutProperties as _objectWithoutProperties, extends as _extends, objectSpread2 as _objectSpread2 } from '../../_virtual/_rollupPluginBabelHelpers.js';
import * as PropTypes from 'prop-types';
import * as React from 'react';
import { TextField } from '../TextField/TextField.js';

var _excluded = ["formatter", "parser", "validators", "onChange", "value", "forwardedRef"];

/* eslint-disable @typescript-eslint/no-explicit-any */

/**
 * TextInputHelper is used for adding validation and formatting to a text field
 */
class TextInputHelper extends React.Component {
  // @ts-ignore
  static getDerivedStateFromProps(props, state) {
    if (typeof props.onChange !== 'undefined') {
      // If a TextInputHelper is given an onChange callback, it's expected the
      // value is being managed outside of the local state.
      var {
        value: _value2
      } = props;
      if (typeof props.formatter !== 'undefined' && state.focusCount === state.blurCount) {
        // Apply formatting if the field is not currently in focus
        _value2 = props.formatter(_value2);
      }
      if (typeof props.parser !== 'undefined' && state.focusCount !== state.blurCount) {
        // Parse the field if the field is being edited
        _value2 = props.parser(_value2);
      }
      return {
        value: _value2
      };
    }
    return null;
  }
  // eslint-disable-next-line @typescript-eslint/member-ordering
  constructor(props) {
    var _this;
    super(props);
    _this = this;
    // eslint-disable-next-line @typescript-eslint/member-ordering
    _defineProperty(this, "refTextField", void 0);
    /**
     * Set focus to the TextField
     */
    // eslint-disable-next-line react/no-unused-class-component-methods
    _defineProperty(this, "focus", () => {
      if (this.refTextField.current) {
        this.refTextField.current.focus();
      }
    });
    /**
     * When the field gains focus the formatting is removed
     */
    _defineProperty(this, "onFocus", e => {
      var {
        value
      } = this.state;

      // Remove formatting
      if (typeof this.props.parser === 'function') {
        value = this.props.parser(value);
      }

      // Update the state
      this.setState({
        focusCount: this.state.focusCount + 1,
        // eslint-disable-line
        value
      });
      if (typeof this.props.onFocus === 'function') {
        this.props.onFocus(e);
      }
    });
    /**
     * When the field loses focuses the value is formatted and the state updated
     * The blur count is tracked as it's used to determine if error state should be displayed
     */
    _defineProperty(this, "onBlur", e => {
      var {
        value
      } = this.state;

      // Apply formatting to the field
      if (typeof this.props.formatter === 'function') {
        value = this.props.formatter(value);
      }

      // Run validation
      this.validate(value, false);

      // Update the state
      var blurCount = this.state.blurCount + 1; // eslint-disable-line
      this.setState({
        blurCount,
        showErrors: blurCount > 0
      });
      if (typeof this.props.onBlur === 'function') {
        this.props.onBlur(e);
      }
    });
    /**
     * When the value changes the field is validated and state updated
     */
    _defineProperty(this, "onChange", (name, value, event) => {
      // Remove formatting
      if (typeof this.props.parser === 'function') {
        value = this.props.parser(value); // eslint-disable-line
      }

      // Run validation
      var validationResult = this.validate(value, false);

      // Update the state
      this.setState(_objectSpread2(_objectSpread2({}, validationResult), {}, {
        value
      }));

      // Notify onchange callback
      if (typeof this.props.onChange !== 'undefined') {
        this.props.onChange(name, value, event);
      }
    });
    _defineProperty(this, "validate", function (value) {
      var externallyTriggered = arguments.length > 1 && arguments[1] !== undefined ? arguments[1] : true;
      // Run validation
      var validationResult = _this.getValidationResults(value);
      var newState = _objectSpread2(_objectSpread2({}, validationResult), {}, {
        value
      });
      if (externallyTriggered) {
        // When the validation is triggered by a blur event the blur event controls the visibility of error messages,
        // when the validation is triggered externally the errors are shown regardless of the blur state
        newState.showErrors = true;
      }

      // Update the state
      _this.setState(newState);
      return validationResult;
    });
    /**
     * Run all validation functions and return a result with error message
     */
    // eslint-disable-next-line
    _defineProperty(this, "getValidationResults", value => {
      var result = {
        errormessage: this.state.errormessage,
        isValid: this.state.isValid
      };
      if (typeof this.props.validators !== 'object' || typeof this.props.validators.length === 'undefined' || this.props.validators.length === 0) {
        return result;
      }

      // The field is valid until it fails a validation rule
      result.isValid = true;
      this.props.validators.forEach(validator => {
        if (!result.isValid) {
          // Don't run anymore validation rules after one fails
          return;
        }

        // Pass into validation function
        result.isValid = validator.method(value || this.state.value);

        // Set the error message when invalid
        if (!result.isValid) {
          result.errormessage = validator.errormessage;
        }
      });
      return result;
    });
    var {
      value: _value
    } = props;
    if (typeof props.formatter !== 'undefined') {
      _value = props.formatter(_value);
    }
    this.state = {
      blurCount: 0,
      errormessage: '',
      focusCount: 0,
      isValid: true,
      showErrors: false,
      value: _value
    };

    // Create a ref if one is not provided
    this.refTextField = props.forwardedRef || /*#__PURE__*/React.createRef();
  }
  /* eslint-disable react/sort-comp */
  render() {
    // Note a "let" is used as the "value" prop is changed depending on whether
    // we use the local state or a parent store for the value.
    /* eslint-disable  prefer-const */
    var _this$props = this.props,
      {
        formatter,
        parser,
        validators,
        onChange,
        value,
        forwardedRef
        // eslint-disable-line
      } = _this$props,
      textfieldprops = _objectWithoutProperties(_this$props, _excluded);
    /* eslint-enable  prefer-const */

    // "value" is copied from props into state
    value = this.state.value;

    // Validation is shown after the field loses focus
    var isValid = true;
    if (!this.state.isValid && this.state.showErrors) {
      isValid = this.state.isValid;
    }
    return /*#__PURE__*/React.createElement(TextField, _extends({}, textfieldprops, {
      value: value,
      onFocus: this.onFocus,
      onChange: this.onChange,
      onBlur: this.onBlur,
      errormessage: this.state.errormessage,
      isValid: isValid,
      ref: this.refTextField
    }));
  }
}

// @ts-ignore 
TextInputHelper.propTypes = {
  formatter: PropTypes.func,
  parser: PropTypes.func,
  validators: PropTypes.arrayOf(PropTypes.shape({
    errormessage: PropTypes.string,
    method: PropTypes.func
  }))
};

export { TextInputHelper };
